/**
 * Utility functions for formatting duration from seconds to user-friendly display
 */

/**
 * Convert seconds to HH:MM format
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration (e.g., "8:30", "40:00")
 */
export function formatDurationHHMM(seconds) {
  if (!seconds && seconds !== 0) return "0:00";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  return `${hours}:${String(minutes).padStart(2, "0")}`;
}

/**
 * Convert seconds to human-readable format
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Human readable duration (e.g., "8 uur 30 min", "40 uur")
 */
export function formatDurationHuman(seconds) {
  if (!seconds && seconds !== 0) return "0 uur";

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (minutes === 0) {
    return `${hours} uur`;
  }

  return `${hours} uur ${minutes} min`;
}

/**
 * Convert seconds to decimal hours for calculations
 * @param {number} seconds - Duration in seconds
 * @returns {number} - Duration in decimal hours (e.g., 8.5 for 8:30)
 */
export function formatDurationDecimal(seconds) {
  if (!seconds && seconds !== 0) return 0;

  return seconds / 3600;
}

/**
 * Convert HH:MM string to seconds (for backward compatibility)
 * @param {string} hhmmString - Duration in HH:MM format
 * @returns {number} - Duration in seconds
 */
export function parseDurationToSeconds(hhmmString) {
  if (!hhmmString) return 0;

  const [hours, minutes] = hhmmString.split(":").map(Number);
  return (hours || 0) * 3600 + (minutes || 0) * 60;
}

/**
 * Convert duration string to hours (backward compatibility)
 * @param {string} durationString - Duration in HH:MM format
 * @returns {number} - Duration in decimal hours
 */
export function parseDurationToHours(durationString) {
  if (!durationString) return 0;

  const seconds = parseDurationToSeconds(durationString);
  return formatDurationDecimal(seconds);
}

/**
 * Convert seconds to hours for backward compatibility
 * @param {number} seconds - Duration in seconds
 * @returns {number} - Duration in decimal hours
 */
export function secondsToHours(seconds) {
  return formatDurationDecimal(seconds);
}
